import { Colors, Lightning } from '@lightningjs/sdk';
import { getFontFaceFromStyle } from 'support/textUtils';
import AbstractLoginModal, {
  AbstractLoginModalTemplateSpec,
} from './AbstractLoginModal';
import { getImageTextureObj } from 'support/generalUtils';
import ActionsList from 'components/common/ActionsList';
import Button from 'components/common/buttons/Button';
import { constants } from 'aliases';
import TextLabel from 'components/common/TextLabel';

export interface AbstractOptionsModalTemplateSpec
  extends AbstractLoginModalTemplateSpec {
  Content: {
    Heading: {
      CheckmarkIcon: Lightning.Texture;
      Text: typeof TextLabel;
    };
    SubHeading: typeof TextLabel;
    OptionsList: ActionsList;
  } & AbstractLoginModalTemplateSpec['Content'];
}

export type ButtonOptions = {
  label: string;
  action: string;
  passSignals: Record<string, string>;
  [additionalProps: string]: unknown;
};

const EMPTY_HEADING_SPACE = 89;
const INNER_CONTENT = 586;
const CONTENT_HEIGHT = INNER_CONTENT + EMPTY_HEADING_SPACE;
const CONTENT_OFFSET_Y = 229;

const MARGIN_SMALL = 8;
const MARGIN_MEDIUM = 12;
const MARGIN_LARGE = 24;

const CHECKMARK_ICON_SIZE = 36;

const HEADING_HEIGHT = 58;
const HEADING_FONT_SIZE = 36;

const SUB_HEADING_FONT_SIZE = 22;
const SUB_HEADING_LINE_HEIGHT = 35;
const SUB_HEADING_WIDTH = 709;

const OPTIONS_LIST_SPACING = 16;
const OPTIONS_WIDTH = 540;
const OPTIONS_HEIGHT = 65;
const OPTIONS_FONT_SIZE = 18;
const OPTIONS_RADIUS = 4;

export default abstract class AbstractOptionsModal
  extends AbstractLoginModal
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractOptionsModalTemplateSpec>
{
  override _showBackButton = false;

  protected static _optionsListDirection: 'row' | 'column' = 'column';
  protected static _subHeadingWidth = SUB_HEADING_WIDTH;

  protected _InnerContent = this.getByRef('Content')!;
  protected _Heading = this._InnerContent.getByRef('Heading')!;
  protected _CheckmarkIcon = this._Heading.getByRef('CheckmarkIcon')!;
  protected _HeaderText = this._Heading.getByRef('Text')!;
  protected _SubHeading = this._InnerContent.getByRef('SubHeading')!;
  protected _OptionsList: ActionsList =
    this._InnerContent.getByRef('OptionsList')!;

  set checkmarkIconVisible(visible: boolean) {
    this._CheckmarkIcon.visible = visible;
  }

  set headingText(text: string) {
    this._HeaderText.text = text;
  }

  set subHeadingText(text: string) {
    this._SubHeading.text = text;
  }

  set optionItems(items: ButtonOptions[]) {
    this.setOptions(items);
  }

  set optionSignals(signals: any) {
    this._OptionsList.signals = { ...this._OptionsList.signals, ...signals };
  }

  static override _template(): Lightning.Component.Template<AbstractOptionsModalTemplateSpec> {
    const checkmarkIcon = getImageTextureObj(
      'static/images/checkmark-circle-green.svg',
      CHECKMARK_ICON_SIZE,
      CHECKMARK_ICON_SIZE,
    );
    const { Content, ...rest } = super._template();

    return {
      Content: {
        ...Content,
        flex: {
          direction: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        },
        y: CONTENT_OFFSET_Y,
        h: CONTENT_HEIGHT,
        color: Colors('appAccent').get(),
        Heading: {
          h: HEADING_HEIGHT,
          flex: {
            direction: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          },
          flexItem: {
            marginBottom: MARGIN_MEDIUM,
          },
          CheckmarkIcon: {
            ...checkmarkIcon,
            flexItem: { marginRight: MARGIN_SMALL, marginBottom: MARGIN_SMALL },
            visible: true,
          },
          Text: {
            text: {
              fontSize: HEADING_FONT_SIZE,
              fontFace: getFontFaceFromStyle('bold'),
              fontColor: Colors('text').get(),
            },
          },
        },
        SubHeading: {
          flexItem: {
            marginBottom: MARGIN_LARGE,
          },
          w: this._subHeadingWidth,
          text: {
            fontSize: SUB_HEADING_FONT_SIZE,
            fontFace: getFontFaceFromStyle('regular'),
            textColor: Colors('text').get(),
            wordWrapWidth: this._subHeadingWidth,
            textAlign: 'center',
            lineHeight: SUB_HEADING_LINE_HEIGHT,
            advancedRenderer: true,
          },
        },
        OptionsList: {
          flex: {
            direction: this._optionsListDirection,
            justifyContent: 'center',
            alignItems: 'center',
          },
          signals: {
            $onHover: '$onHover',
          },
          passSignals: {
            $onHover: true,
          },
          type: ActionsList,
          spacing: OPTIONS_LIST_SPACING,
          items: [],
        },
      },
      ...rest,
    };
  }

  get title() {
    return [
      this._HeaderText.text?.text ?? '',
      this._SubHeading.text?.text ?? '',
    ];
  }

  override $onHover(target: Lightning.Component) {
    super.$onHover(target);
    if (target === this._OptionsList) {
      this._setState('OptionsList');
    }
  }

  private setOptions(items: ButtonOptions[]) {
    const styledItems = items.map(item => ({
      type: Button,
      minWidth: OPTIONS_WIDTH,
      height: OPTIONS_HEIGHT,
      fontSize: OPTIONS_FONT_SIZE,
      radius: OPTIONS_RADIUS,
      signals: { $onHover: true },
      zIndex: constants.ui.zIndex.foregroundLayer + 2,
      ...item,
    }));

    this._OptionsList.items = styledItems;
  }

  static override _states() {
    return [
      ...super._states(),
      class Content extends this {
        override _handleUp() {
          if (this._showBackButton) this._setState('Header');
        }

        override _handleDown() {
          this._setState('OptionsList');
        }
      },
      class OptionsList extends this {
        override _getFocused() {
          return this._OptionsList;
        }

        override _handleDown() {
          if (this._showSkipButton) {
            this._setState('SkipButton');
          }
        }

        override _handleUp() {
          if (this._showBackButton) {
            this._setState('Header');
          }
        }
      },
    ];
  }
}
